import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { prepareGeolocation } from '@/utils/geolocation';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAndroidBackButton } from '@/hooks/useAndroidBackButton';
import { useIosSwipeBack } from '@/hooks/useIosSwipeBack';
import { useAppDeepLink } from '@/hooks/useAppDeepLink';
import { applyNativeWebGuards } from '@/utils/nativeWebGuards';
import { disableKeyboardAccessory, scheduleHideNativeSplash } from '@/utils/nativeShell';
import { hasSeenPushPrompt, markPushPromptSeen } from '@/utils/pushPrompt';
import { PushPermissionSheet, shouldShowPushPrompt } from '@/components/native/PushPermissionSheet';

export function useNativeInit() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [promptAccepted, setPromptAccepted] = useState(false);
  const [promptSeen, setPromptSeen] = useState(hasSeenPushPrompt);

  usePushNotifications(user?.user_id, isLoading, isAuthenticated, promptAccepted);
  useAndroidBackButton();
  useIosSwipeBack();
  useAppDeepLink();

  useEffect(() => {
    const removeGuards = applyNativeWebGuards();
    void disableKeyboardAccessory();
    const cancelSplashHide = scheduleHideNativeSplash();
    void prepareGeolocation();

    return () => {
      removeGuards();
      cancelSplashHide();
    };
  }, []);

  const showPrompt = shouldShowPushPrompt({
    isAuthenticated,
    authLoading: isLoading,
  }) && !promptSeen;

  return {
    pushPrompt: {
      open: showPrompt,
      onAllow: () => {
        markPushPromptSeen();
        setPromptSeen(true);
        setPromptAccepted(true);
      },
      onLater: () => {
        markPushPromptSeen();
        setPromptSeen(true);
      },
    },
  };
}

export function NativeInit() {
  const { pushPrompt } = useNativeInit();
  return <PushPermissionSheet {...pushPrompt} />;
}
